// Browser polyfill for process (required for Azure SDK)
import process from 'process';
import { appConfig } from '../config/appConfig';

// Extend Window interface for TypeScript
declare global {
  interface Window {
    process: typeof process;
  }
}

// Set up the polyfill
window.process = process;

// Backend API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL 
  ? `${process.env.REACT_APP_API_URL}/api` 
  : '/api';

// Types for enhanced feedback
export interface ToolCallDetail {
  id: string;
  name: string;
  arguments?: any;
  result?: any;
  timestamp?: string;
  duration_ms?: number;
  status?: 'success' | 'error' | 'pending';
  error?: string;
}

export interface ConversationMessage {
  id: string;
  sender: 'user' | 'agent' | 'system';
  content: string;
  timestamp: string;
  agentName?: string;
  isCompleted?: boolean;
  toolCalls?: string[];
  a2aRouting?: any;
}

class FeedbackService {
  private feedbackCount = 0;

  // Add feedback entry via backend proxy (ENHANCED VERSION)
  async addFeedback(
    messageId: string | undefined,
    agentName: string,
    userMessage: string,
    agentMessage: string,
    feedback: 'positive' | 'negative',
    userEmail: string,
    messageCount: number,
    toolCalls?: string[],
    a2aRouting?: {
      originalAgent: string;
      currentAgent: string;
      previousAgent?: string;
      reason?: string;
    },
    feedbackTags?: string[],
    feedbackNotes?: string,
    // NEW ENHANCED PARAMETERS
    conversationHistory?: ConversationMessage[],
    toolCallsDetailed?: ToolCallDetail[],
    metadata?: Record<string, any>
  ): Promise<boolean> {
    try {
      console.log('🎯 Enhanced feedback submission started:', {
        agentName,
        feedback,
        userEmail: userEmail ? '***@' + userEmail.split('@')[1] : 'NO EMAIL',
        messageCount,
        userMessageLength: userMessage.length,
        agentMessageLength: agentMessage.length,
        conversationHistoryLength: conversationHistory?.length || 0,
        toolCallsDetailedCount: toolCallsDetailed?.length || 0,
        legacyToolCallsCount: toolCalls?.length || 0,
        hasMetadata: !!metadata,
        metadataKeys: metadata ? Object.keys(metadata) : []
      });

      // Validate inputs
      if (!userEmail) {
        console.error('❌ No user email provided for feedback');
        return false;
      }

      if (!agentName) {
        console.error('❌ No agent name provided for feedback');
        return false;
      }

      // Prepare enhanced request payload
      const payload = {
        messageId,
        agentName,
        userMessage: userMessage.replace(/\n/g, ' ').trim(),
        agentMessage: agentMessage.replace(/\n/g, ' ').trim(),
        feedback,
        userEmail,
        messageCount,
        toolCalls: toolCalls || [],
        a2aRouting: a2aRouting || null,
        // Optional structured fields for negative feedback
        feedbackTags: Array.isArray(feedbackTags) ? feedbackTags : undefined,
        feedbackNotes: typeof feedbackNotes === 'string' ? feedbackNotes : undefined,
        
        // NEW ENHANCED FIELDS
        conversationHistory: conversationHistory || [],
        toolCallsDetailed: toolCallsDetailed || [],
        metadata: metadata || undefined
      };

      console.log('📝 Sending enhanced feedback to backend:', {
        agentName: payload.agentName,
        feedback: payload.feedback,
        messageCount: payload.messageCount,
        conversationMessageCount: payload.conversationHistory.length,
        toolCallDetailCount: payload.toolCallsDetailed.length,
        hasMetadata: !!payload.metadata
      });

      // Send request to backend proxy
      const response = await fetch(`${API_BASE_URL}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        this.feedbackCount++;
        console.log('✅ Enhanced feedback saved successfully:', {
          uid: result.uid,
          agentName: payload.agentName,
          feedback: payload.feedback,
          conversationMessagesCaptured: result.metadata?.conversationMessagesCaptured || 0,
          toolCallsCaptured: result.metadata?.toolCallsCaptured || 0,
          totalEntries: this.feedbackCount
        });

        // Show user notification
        this.showUserNotification('Feedback submitted successfully', 'success');
        return true;
      } else {
        console.error('❌ Backend responded with error:', {
          status: response.status,
          error: result.error || 'Unknown error'
        });
        
        this.showUserNotification(`Failed to save feedback: ${result.error}`, 'error');
        return false;
      }

    } catch (error: any) {
      console.error('❌ Error submitting feedback:', error);
      
      // Handle network errors
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        console.warn('🔒 Network error - is the backend server running?');
        this.showUserNotification('Cannot connect to server', 'error');
      } else {
        this.showUserNotification('Failed to save feedback', 'error');
      }
      
      return false;
    }
  }

  // Show user notification with modern design
  private showUserNotification(message: string, type: 'success' | 'error'): void {
    try {
      // Detect dark mode
      const isDarkMode = document.documentElement.classList.contains('dark') || 
                        document.body.classList.contains('dark') ||
                        window.matchMedia('(prefers-color-scheme: dark)').matches;
      
      // Create notification container
      const notification = document.createElement('div');
      
      // Icon SVGs (Lucide icons)
      const successIcon = `
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <path d="m9 12 2 2 4-4"></path>
        </svg>
      `;
      
      const errorIcon = `
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" x2="12" y1="8" y2="12"></line>
          <line x1="12" x2="12.01" y1="16" y2="16"></line>
        </svg>
      `;
      
      const icon = type === 'success' ? successIcon : errorIcon;
      
      // Theme-aware colors
      const colors = {
        success: {
          icon: type === 'success' ? '#10b981' : '#ef4444',
          border: isDarkMode ? (type === 'success' ? '#065f46' : '#7f1d1d') : (type === 'success' ? '#d1fae5' : '#fee2e2'),
          bg: isDarkMode ? '#2b2e32' : '#ffffff',
          text: isDarkMode ? '#f8f9fa' : '#2b2e32'
        }
      };
      
      notification.innerHTML = `
        <div style="display: flex; align-items: center; gap: 12px;">
          <div style="flex-shrink: 0; display: flex; align-items: center; color: ${colors.success.icon};">
            ${icon}
          </div>
          <div style="flex: 1; font-size: 14px; line-height: 1.5; color: ${colors.success.text};">
            ${message}
          </div>
        </div>
      `;
      
      notification.style.cssText = `
        position: fixed;
        top: 24px;
        right: 24px;
        padding: 16px 20px;
        border-radius: 12px;
        background: ${colors.success.bg};
        font-weight: 500;
        z-index: 10000;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        max-width: 400px;
        box-shadow: 0 10px 25px -5px rgba(0, 0, 0, ${isDarkMode ? '0.3' : '0.1'}), 
                    0 10px 10px -5px rgba(0, 0, 0, ${isDarkMode ? '0.2' : '0.04'});
        border: 1px solid ${colors.success.border};
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
        opacity: 0;
        transform: translateX(100px) scale(0.95);
      `;
      
      document.body.appendChild(notification);
      
      // Animate in
      requestAnimationFrame(() => {
        notification.style.opacity = '1';
        notification.style.transform = 'translateX(0) scale(1)';
      });
      
      // Remove after 4 seconds
      setTimeout(() => {
        notification.style.opacity = '0';
        notification.style.transform = 'translateX(100px) scale(0.95)';
        setTimeout(() => {
          if (document.body.contains(notification)) {
            document.body.removeChild(notification);
          }
        }, 300);
      }, 4000);
    } catch (notificationError) {
      console.warn('Could not show notification:', notificationError);
    }
  }

  // Get current feedback count
  getCurrentFeedbackCount(): number {
    return this.feedbackCount;
  }

  // Get feedback summary from backend
  async getFeedbackSummary(): Promise<{ positive: number; negative: number; total: number }> {
    try {
      const response = await fetch(`${API_BASE_URL}/feedback/stats`);
      const result = await response.json();
      
      if (response.ok && result.success) {
        return result.stats;
      } else {
        console.warn('Failed to get feedback stats:', result.error);
        return { positive: 0, negative: 0, total: this.feedbackCount };
      }
    } catch (error) {
      console.warn('Error getting feedback summary:', error);
      return { positive: 0, negative: 0, total: this.feedbackCount };
    }
  }

  // Initialize service
  async initialize(): Promise<void> {
    try {
      console.log('📋 Enhanced Feedback Service Initialized');
      console.log('🔗 Backend API:', `${API_BASE_URL}/feedback`);
      console.log('✨ Features: Conversation History + Tool Call Details');
    } catch (error) {
      console.error('❌ Failed to initialize feedback service:', error);
      this.showUserNotification('⚠️ Feedback service initialization failed', 'error');
    }
  }
}

// Create singleton instance
export const feedbackService = new FeedbackService();

// Auto-initialize
feedbackService.initialize().catch(console.error);

export default FeedbackService; 